// frontend/src/pages/ContaReceber/CadastrarContaReceber.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import FormLabel from "../../components/FormLabel";
import { maskCurrency, formatCurrencyFromDb } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";
import { formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import usePermission from "../../hooks/usePermission";
import config from "../../config";

const CadastrarContaReceber = ({ contaReceberId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(contaReceberId);
    const podeCriar = usePermission("CRIAR_CONTAS_RECEBER");
    const podeDeletar = usePermission("DELETAR_CONTAS_RECEBER");
    const podeAtualizar = usePermission("ATUALIZAR_CONTAS_RECEBER");
    const podeExportar = usePermission("EXPORTAR_CONTAS_RECEBER");

    // Opções para selects
    const [obras, setObras] = useState([]);
    const [orcamentos, setOrcamentos] = useState([]);

    // Arrays para armazenar lançamentos (já cadastrados e novos)
    const [medicoesObra, setMedicoesObra] = useState([]);
    const [liberacoes, setLiberacoes] = useState([]);
    const [notasFiscais, setNotasFiscais] = useState([]);

    // Estados para os novos lançamentos (entrada dos formulários)
    const [newLiberacao, setNewLiberacao] = useState({
        obraId: "",
        dataInicio: "",
        dataFinal: "",
        valor: "",
        percentual: "",
        orcamentoId: ""
    });

    // Estados dos filtros globais
    const [filterObra, setFilterObra] = useState("");
    const [filterEmpenho, setFilterEmpenho] = useState("");

    // Estados para itens removidos (para exclusão no backend)
    const [removedLiberacoes, setRemovedLiberacoes] = useState([]);

    // Estados de paginação para cada tabela
    const [medicoesObraPage, setMedicoesObraPage] = useState(1);
    const medicoesObraPageSize = 10;
    const [medicoesObraTotal, setMedicoesObraTotal] = useState(0);

    const [liberacoesPage] = useState(1);
    const liberacoesPageSize = 10;
    const [liberacoesTotal, setLiberacoesTotal] = useState(0);

    const [notasPage, setNotasPage] = useState(1);
    const notasPageSize = 10;
    const [notasTotal, setNotasTotal] = useState(0);

    const [showDialog, setShowDialog] = useState(false); // Estado para controlar o modal    
    const [selectedPdf, setSelectedPdf] = useState("");

    const selectedObra = obras.find(
        (obra) => parseInt(obra.id, 10) === parseInt(filterObra, 10)
    );

    // Funções genéricas para atualizar estado
    const handleChange = (e, setter) => {
        const { name, value } = e.target;
        setter((prev) => ({ ...prev, [name]: value }));
    };

    const handleCurrencyChange = (e, setter) => {
        const { name, value } = e.target;
        setter((prev) => ({ ...prev, [name]: maskCurrency(value) }));
    };

    // Handlers para novos lançamentos – Liberações
    const handleNewLiberacaoChange = (e) => {
        handleChange(e, setNewLiberacao);
    };

    const handleNewLiberacaoCurrencyChange = (e) => {
        handleCurrencyChange(e, setNewLiberacao);
    };

    const convertValue = (value) => {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            // Remove o "R$" e pontos, substitui vírgula por ponto e faz o parse
            const cleaned = value.replace(/[R$\s.]/g, "").replace(",", ".");
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : num;
        }
        return 0;
    };

    // Função para buscar opções e dados paginados
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const obrasRes = await api.get("/obras/listar/todas");
                setObras(obrasRes.data);
                const orcamentosRes = await api.get("/orcamentos/todos");
                setOrcamentos(orcamentosRes.data);
            } catch (error) {
                console.error("Erro ao carregar opções:", error);
            }
        };

        fetchOptions();
    }, []);

    // Fetch para medições da obra baseado no filtro global
    useEffect(() => {
        const fetchMedicoesObra = async () => {
            try {
                let url;
                if (filterObra) {
                    // Se tem filtro de obra, busca medições dessa obra específica
                    url = `/obras/${filterObra}/medicoes?page=${medicoesObraPage}&pageSize=${medicoesObraPageSize}`;
                } else {
                    // Se não tem filtro, busca medições de todas as obras
                    url = `/medicoes/todas?page=${medicoesObraPage}&pageSize=${medicoesObraPageSize}`;
                }
                
                const medicoesRes = await api.get(url);
                setMedicoesObra(medicoesRes.data.records);
                setMedicoesObraTotal(medicoesRes.data.total);
            } catch (error) {
                console.error("Erro ao carregar medições:", error);
                setMedicoesObra([]);
                setMedicoesObraTotal(0);
            }
        };
        fetchMedicoesObra();
    }, [filterObra, medicoesObraPage]);

    // Fetch para liberações com paginação
    useEffect(() => {
        const fetchLiberacoes = async () => {
            try {
                const liberacoesRes = await api.get(
                    `/contasreceber/liberacoes?page=${liberacoesPage}&pageSize=${liberacoesPageSize}`
                );
                setLiberacoes(liberacoesRes.data.records);
                setLiberacoesTotal(liberacoesRes.data.total);
            } catch (error) {
                console.error("Erro ao carregar liberações:", error);
            }
        };
        fetchLiberacoes();
    }, [liberacoesPage]);

    // Fetch para notas fiscais vinculadas às obras
    useEffect(() => {
        const fetchNotasFiscais = async () => {
            try {
                let url;
                if (filterObra) {
                    // Se tem filtro de obra, busca notas fiscais dessa obra específica
                    url = `/obras/${filterObra}/notas-fiscais?page=${notasPage}&pageSize=${notasPageSize}`;
                } else {
                    // Se não tem filtro, busca todas as notas fiscais vinculadas a obras
                    url = `/obras/notas-fiscais/todas?page=${notasPage}&pageSize=${notasPageSize}`;
                }
                
                const notasRes = await api.get(url);
                setNotasFiscais(notasRes.data.records);
                setNotasTotal(notasRes.data.total);
            } catch (error) {
                console.error("Erro ao carregar notas fiscais:", error);
                setNotasFiscais([]);
                setNotasTotal(0);
            }
        };
        fetchNotasFiscais();
    }, [notasPage, filterObra]);

    // ––––––– MEDIÇÕES –––––––
    // useEffect para cálculo de percentual comentado - não mais necessário
    /*
    useEffect(() => {
        if (newMedicao.obraId && newMedicao.valor) {
            const obraSelecionada = obras.find(
                (obra) => parseInt(obra.id, 10) === parseInt(newMedicao.obraId, 10)
            );
            if (obraSelecionada && obraSelecionada.orcamentoProjetoReceber) {
                // Converte para string antes de passar para o parseCurrencyToFloat
                const valorMedicao = parseCurrencyToFloat(newMedicao.valor.toString());
                const orcamentoReceber = parseCurrencyToFloat(
                    obraSelecionada.orcamentoProjetoReceber.toString()
                );
                if (orcamentoReceber > 0) {
                    const percentualCalculado = ((valorMedicao / orcamentoReceber) * 100) * 100;
                    setNewMedicao((prev) => ({
                        ...prev,
                        percentual: percentualCalculado.toFixed(2)
                    }));
                } else {
                    setNewMedicao((prev) => ({ ...prev, percentual: "0" }));
                }
            }
        } else {
            setNewMedicao((prev) => ({ ...prev, percentual: "" }));
        }
    }, [newMedicao.valor, newMedicao.obraId, obras]);
    */

    // Funções de medições comentadas - agora apenas visualização
    /*
    const handleEditMedicao = (id) => {
        setMedicoes((prev) =>
            prev.map((item) =>
                item.id === id
                    ? { ...item, isEditing: true, tempData: { ...item } }
                    : item
            )
        );
    };

    const handleMedicaoFieldChange = (id, field, value) => {
        setMedicoes((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    const handleSaveMedicao = async (id) => {
        const item = medicoes.find((i) => i.id === id);
        if (!item) return;
        if (item.isNew) {
            setMedicoes((prev) =>
                prev.map((i) => (i.id === id ? { ...i, isEditing: false } : i))
            );
        } else {
            try {
                await api.put(`/contasreceber/medicoes/${id}`, {
                    obraId: parseInt(item.obraId, 10),
                    dataInicio: item.dataInicio,
                    dataFinal: item.dataFinal,
                    valor: parseCurrencyToFloat(item.valor),
                    percentual: parseFloat(item.percentual),
                    orcamentoId: item.orcamentoId ? parseInt(item.orcamentoId, 10) : null
                });
                setMedicoes((prev) =>
                    prev.map((i) =>
                        i.id === id ? { ...i, isEditing: false, tempData: undefined } : i
                    )
                );
            } catch (error) {
                console.error("Erro ao atualizar medição:", error);
                alert("Erro ao atualizar medição.");
            }
        }
    };

    const handleCancelEditMedicao = (id) => {
        setMedicoes((prev) =>
            prev.map((item) =>
                item.id === id
                    ? item.isNew
                        ? { ...item, isEditing: false }
                        : { ...item.tempData, isEditing: false, tempData: undefined }
                    : item
            )
        );
    };

    // Funções de medições comentadas - agora apenas visualização
    /*
    const handleAddMedicao = () => {
        if (
            !newMedicao.obraId ||
            !newMedicao.dataInicio ||
            !newMedicao.dataFinal ||
            !newMedicao.valor ||
            !newMedicao.percentual
        ) {
            alert("Preencha os campos obrigatórios para a medição.");
            return;
        }
        setMedicoes((prev) => [
            ...prev,
            { id: Date.now(), ...newMedicao, isNew: true }
        ]);
        setNewMedicao({
            obraId: "",
            dataInicio: "",
            dataFinal: "",
            valor: "",
            percentual: "",
            orcamentoId: ""
        });
    };

    const handleRemoveMedicao = (id) => {
        const item = medicoes.find((i) => i.id === id);
        if (item && !item.isNew) {
            setRemovedMedicoes((prev) => [...prev, id]);
        }
        setMedicoes((prev) => prev.filter((item) => item.id !== id));
    };
    */

    // ––––––– LIBERAÇÕES –––––––

    const handleEditLiberacao = (id) => {
        setLiberacoes((prev) =>
            prev.map((item) =>
                item.id === id
                    ? { ...item, isEditing: true, tempData: { ...item } }
                    : item
            )
        );
    };

    const handleLiberacaoFieldChange = (id, field, value) => {
        setLiberacoes((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, [field]: value } : item
            )
        );
    };

    const handleSaveLiberacao = async (id) => {
        const item = liberacoes.find((i) => i.id === id);
        if (!item) return;
        if (item.isNew) {
            setLiberacoes((prev) =>
                prev.map((i) => (i.id === id ? { ...i, isEditing: false } : i))
            );
        } else {
            try {
                await api.put(`/contasreceber/liberacoes/${id}`, {
                    obraId: parseInt(item.obraId, 10),
                    dataInicio: item.dataInicio,
                    dataFinal: item.dataFinal,
                    valor: parseCurrencyToFloat(item.valor),
                    percentual: parseFloat(item.percentual),
                    orcamentoId: item.orcamentoId ? parseInt(item.orcamentoId, 10) : null
                });
                setLiberacoes((prev) =>
                    prev.map((i) =>
                        i.id === id ? { ...i, isEditing: false, tempData: undefined } : i
                    )
                );
            } catch (error) {
                console.error("Erro ao atualizar liberação:", error);
                alert("Erro ao atualizar liberação.");
            }
        }
    };

    const handleCancelEditLiberacao = (id) => {
        setLiberacoes((prev) =>
            prev.map((item) =>
                item.id === id
                    ? item.isNew
                        ? { ...item, isEditing: false }
                        : { ...item.tempData, isEditing: false, tempData: undefined }
                    : item
            )
        );
    };

    const handleAddLiberacao = () => {
        if (
            !newLiberacao.obraId ||
            !newLiberacao.dataInicio ||
            !newLiberacao.dataFinal ||
            !newLiberacao.valor ||
            !newLiberacao.percentual
        ) {
            alert("Preencha os campos obrigatórios para a liberação.");
            return;
        }
        setLiberacoes((prev) => [
            ...prev,
            { id: Date.now(), ...newLiberacao, isNew: true }
        ]);
        setNewLiberacao({
            obraId: "",
            dataInicio: "",
            dataFinal: "",
            valor: "",
            percentual: "",
            orcamentoId: ""
        });
    };

    const handleRemoveLiberacao = (id) => {
        const item = liberacoes.find((i) => i.id === id);
        if (item && !item.isNew) {
            setRemovedLiberacoes((prev) => [...prev, id]);
        }
        setLiberacoes((prev) => prev.filter((item) => item.id !== id));
    };

    // Filtros globais - medições não precisam mais de filtro pois são específicas da obra selecionada
    const filteredMedicoes = medicoesObra;

    const filteredLiberacoes = liberacoes.filter((item) => {
        const matchObra =
            !filterObra || parseInt(item.obraId, 10) === parseInt(filterObra, 10);
        const matchEmpenho =
            !filterEmpenho ||
            (item.orcamentoId &&
                parseInt(item.orcamentoId, 10) === parseInt(filterEmpenho, 10));
        return matchObra && matchEmpenho;
    });

    // Notas fiscais já vêm filtradas do backend por obra
    const filteredNotasFiscais = notasFiscais;

    function parseCurrencyToFloatExcel(value) {
        if (typeof value === 'string') {
            // Remove símbolos monetários, espaços e pontos de milhar, converte vírgula em ponto decimal
            return parseFloat(value.replace(/[^\d,-]/g, '')
                .replace(/\./g, '')
                .replace(',', '.'));
        }
        return value;
    }

    // Função para exportar os dados para Excel
    const handleExportExcel = async () => {
        try {
            const monetaryFields = ['Valor'];

            // Buscar todos os dados para exportação (usando pageSize muito grande para trazer tudo)
            const [medicoesResponse, notasResponse] = await Promise.all([
                // Buscar todas as medições
                filterObra 
                    ? api.get(`/obras/${filterObra}/medicoes?pageSize=10000`)
                    : api.get(`/medicoes/todas?pageSize=10000`),
                // Buscar todas as notas fiscais
                filterObra 
                    ? api.get(`/obras/${filterObra}/notas-fiscais?pageSize=10000`)
                    : api.get(`/obras/notas-fiscais/todas?pageSize=10000`)
            ]);

            const todasMedicoes = medicoesResponse.data.records || [];
            const todasNotasFiscais = Array.isArray(notasResponse.data.records) 
                ? notasResponse.data.records 
                : Array.isArray(notasResponse.data) 
                    ? notasResponse.data 
                    : [];

            const dataMedicoes = todasMedicoes.map((item) => {
                const obra = obras.find((o) => o.id === parseInt(item.obraId, 10))?.nomeProjeto || "-";
                const empenho = orcamentos.find((o) => o.id === parseInt(item.orcamentoId, 10))?.codigoOrcamento || "-";
                return {
                    "Obra": obra,
                    "Número da Medição": item.numeroMedicao || "-",
                    "Valor Medido": item.valorMedido || "-",
                    "Percentual": item.percentualExecucao ? `${item.percentualExecucao}%` : "-",
                    "Número NF": item.numeroNotaFiscal || "-",
                    "Período": item.periodo || "-",
                    "Empenho": empenho
                };
            });

            const dataNotasFiscais = todasNotasFiscais.map((nota) => {
                return {
                    "Tipo": nota.tipo === 'material' ? 'Material' : 'Serviço',
                    "Número": nota.numero || "-",
                    "Série": nota.serie || "-",
                    "Data de Emissão": nota.dataEmissao ? formatDateWithoutHour(nota.dataEmissao) : "-",
                    "Valor": nota.valor || "-",
                    "Fornecedor": nota.fornecedor ? nota.fornecedor.nome : "-",
                    "CNPJ Fornecedor": nota.fornecedor ? nota.fornecedor.cnpj : "-",
                    "Obra": nota.obra ? nota.obra.nome : "-",
                    "Status": nota.processada ? "Processada" : "Pendente",
                    "Chave NFe": nota.chave || "-"
                };
            });

            const wsMedicoes = XLSX.utils.json_to_sheet(dataMedicoes);
            const wsNotas = XLSX.utils.json_to_sheet(dataNotasFiscais);

            [wsMedicoes, wsNotas].forEach((worksheet) => {
                const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
                monetaryFields.forEach((field) => {
                    const colIndex = headers.indexOf(field);
                    if (colIndex >= 0) {
                        const columnLetter = XLSX.utils.encode_col(colIndex);
                        const range = XLSX.utils.decode_range(worksheet['!ref']);

                        for (let i = range.s.r + 1; i <= range.e.r; i++) {
                            const cellAddress = `${columnLetter}${i + 1}`;
                            if (worksheet[cellAddress]) {
                                worksheet[cellAddress].t = 'n';
                                worksheet[cellAddress].z = '#,##0.00';
                            }
                        }
                    }
                });
            });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, wsMedicoes, "Medições");
            XLSX.utils.book_append_sheet(wb, wsNotas, "Notas Fiscais Vinculadas");

            const nomeArquivo = filterObra && selectedObra 
                ? `relatorio_${selectedObra.nomeProjeto.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`
                : "relatorio_conta_receber.xlsx";
                
            XLSX.writeFile(wb, nomeArquivo);
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            alert('Erro ao exportar dados para Excel');
        }
    };

    // Cálculo do total de páginas
    const medicoesTotalPages = Math.ceil(medicoesObraTotal / medicoesObraPageSize);
    const liberacoesTotalPages = Math.ceil(liberacoesTotal / liberacoesPageSize);
    const notasTotalPages = Math.ceil(notasTotal / notasPageSize);

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    return (
        <Template>
            {/* Filtros Globais */}
            <div className="flex gap-4 mb-6">
                <div className="flex flex-col">
                    <FormLabel htmlFor="filterObra">Filtrar por Obra</FormLabel>
                    <select
                        id="filterObra"
                        name="filterObra"
                        value={filterObra}
                        onChange={(e) => setFilterObra(e.target.value)}
                        className="mt-1 px-3 py-2 bg-white rounded-md border border-slate-300"
                    >
                        <option value="">Todas</option>
                        {obras.map((obra) => (
                            <option key={obra.id} value={obra.id}>
                                {obra.nomeProjeto}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col">
                    <FormLabel htmlFor="filterEmpenho">Filtrar por Empenho</FormLabel>
                    <select
                        id="filterEmpenho"
                        name="filterEmpenho"
                        value={filterEmpenho}
                        onChange={(e) => setFilterEmpenho(e.target.value)}
                        className="mt-1 px-3 py-2 bg-white rounded-md border border-slate-300"
                    >
                        <option value="">Todos</option>
                        {orcamentos.map((orc) => (
                            <option key={orc.id} value={orc.id}>
                                {orc.codigoOrcamento}
                            </option>
                        ))}
                    </select>
                </div>
                {/* Botão para exportar para Excel */}
                <div className="flex items-end">
                    {podeExportar && (
                        <button
                            type="button"
                            onClick={handleExportExcel}
                            className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer"
                        >
                            <img src="/images/icon/upload.png" alt="Exportar" className="w-4 h-4" />
                            <span className="text-sm font-medium">Exportar</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        Relatório de Contas a Receber
                    </h1>
                    {selectedObra && selectedObra.orcamentoProjetoReceber && (
                        <div className="mt-2 p-4 bg-yellow-100 text-yellow-800 font-bold rounded-md">
                            Valor a Receber: {formatCurrencyFromDb(selectedObra.orcamentoProjetoReceber)}
                        </div>
                    )}
                </div>

                {/* Seção de Mediçōes */}
                <div className="border p-4 rounded-md">
                    <h2 className="text-xl font-medium text-stone-700 mb-4">Medições das Obras</h2>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-700">
                            {filterObra 
                                ? `Exibindo medições da obra selecionada no filtro global.`
                                : `Exibindo medições de todas as obras. Use o filtro global "Filtrar por Obra" para ver medições de uma obra específica.`
                            }
                        </p>
                    </div>
                    {/* Tabela de Mediçōes apenas para visualização */}
                    <div className="overflow-x-auto overflow-y-auto max-h-80 mt-4">
                        <table className="min-w-full bg-white border border-slate-300">
                            <thead>
                                <tr>
                                    {!filterObra && <th className="px-4 py-2 bg-stone-200">Obra</th>}
                                    <th className="px-4 py-2 bg-stone-200">Número da Medição</th>
                                    <th className="px-4 py-2 bg-stone-200">Valor Medido</th>
                                    <th className="px-4 py-2 bg-stone-200">Percentual</th>
                                    <th className="px-4 py-2 bg-stone-200">Número NF</th>
                                    <th className="px-4 py-2 bg-stone-200">Período</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMedicoes.length > 0 ? (
                                    filteredMedicoes.map((item) => (
                                        <tr key={item.id}>
                                            {!filterObra && (
                                                <td className="border px-4 py-2">
                                                    {item.obra?.nomeProjeto || "-"}
                                                </td>
                                            )}
                                            <td className="border px-4 py-2">
                                                {item.numeroMedicao || "-"}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {formatCurrencyFromDb(item.valorMedido)}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {item.percentualExecucao ? `${item.percentualExecucao}%` : "-"}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {item.numeroNotaFiscal || "-"}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {item.periodo || "-"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={filterObra ? "5" : "6"} className="border px-4 py-2 text-center">
                                            {filterObra ? "Nenhuma medição encontrada para a obra selecionada" : "Nenhuma medição encontrada"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Paginação para Mediçōes */}
                    <div className="flex justify-between items-center mt-2">
                        <button
                            type="button"
                            onClick={() => setMedicoesObraPage((prev) => Math.max(prev - 1, 1))}
                            disabled={medicoesObraPage === 1}
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex disabled:opacity-50"
                                }`}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" />
                        </button>
                        <span className="text-sm">
                            Página {medicoesObraPage} de {medicoesTotalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() =>
                                setMedicoesObraPage((prev) =>
                                    prev < medicoesTotalPages ? prev + 1 : prev
                                )
                            }
                            disabled={medicoesObraPage >= medicoesTotalPages}
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex disabled:opacity-50"
                            }`}

                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" />
                        </button>
                    </div>
                </div>

                {/* ––––––– NOTAS FISCAIS VINCULADAS ÀS OBRAS ––––––– */}
                <div className="border p-4 rounded-md">
                    <h2 className="text-xl font-medium text-stone-700 mb-4">
                        Notas Fiscais Vinculadas às Obras
                        {filterObra && selectedObra && (
                            <span className="text-sm font-normal text-stone-500 ml-2">
                                - {selectedObra.nomeProjeto}
                            </span>
                        )}
                    </h2>
                    
                    {/* Tabela de Notas Fiscais (apenas visualização) */}
                    <div className="overflow-x-auto overflow-y-auto max-h-80">
                        <table className="min-w-full bg-white border border-slate-300">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-stone-200">Tipo</th>
                                    <th className="px-4 py-2 bg-stone-200">Número</th>
                                    <th className="px-4 py-2 bg-stone-200">Data de Emissão</th>
                                    <th className="px-4 py-2 bg-stone-200">Valor</th>
                                    <th className="px-4 py-2 bg-stone-200">Fornecedor</th>
                                    <th className="px-4 py-2 bg-stone-200">Obra</th>
                                    <th className="px-4 py-2 bg-stone-200">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notasFiscais.length > 0 ? (
                                    notasFiscais.map((nota) => (
                                        <tr key={`${nota.tipo}-${nota.id}`} className="hover:bg-gray-50">
                                            <td className="border px-4 py-2">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    nota.tipo === 'material' 
                                                        ? 'bg-blue-100 text-blue-800' 
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {nota.tipo === 'material' ? 'Material' : 'Serviço'}
                                                </span>
                                            </td>
                                            <td className="border px-4 py-2">
                                                {nota.numero || '-'}
                                                {nota.serie && (
                                                    <span className="text-sm text-gray-500 ml-1">
                                                        (Série: {nota.serie})
                                                    </span>
                                                )}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {nota.dataEmissao ? formatDateWithoutHour(nota.dataEmissao) : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {nota.valor ? formatCurrencyFromDb(nota.valor) : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {nota.fornecedor ? (
                                                    <div>
                                                        <div className="font-medium">{nota.fornecedor.nome}</div>
                                                        <div className="text-sm text-gray-500">{nota.fornecedor.cnpj}</div>
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                                {nota.obra ? nota.obra.nome : '-'}
                                            </td>
                                            <td className="border px-4 py-2">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    nota.processada 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {nota.processada ? 'Processada' : 'Pendente'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-gray-500">
                                            {filterObra 
                                                ? "Nenhuma nota fiscal vinculada a esta obra." 
                                                : "Nenhuma nota fiscal vinculada às obras encontrada."
                                            }
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Paginação para Notas Fiscais */}
                    {notasTotalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                            <button
                                type="button"
                                onClick={() => setNotasPage((prev) => Math.max(prev - 1, 1))}
                                disabled={notasPage === 1}
                                className="w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex disabled:opacity-50"
                            >
                                <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" alt="Página anterior" />
                            </button>
                            <span className="text-sm">
                                Página {notasPage} de {notasTotalPages} ({notasTotal} notas)
                            </span>
                            <button
                                type="button"
                                onClick={() => setNotasPage((prev) => (prev < notasTotalPages ? prev + 1 : prev))}
                                disabled={notasPage >= notasTotalPages}
                                className="w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex disabled:opacity-50"
                            >
                                <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" alt="Próxima página" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showDialog && (
                <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                    <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                        <div className="flex justify-between items-center p-4">
                            <div className="flex items-center gap-4">
                                <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                    <img className="w-6 h-6 relative  overflow-hidden" src="images/icon/download.png" />
                                </a>
                                <div className="w-6 h-6 relative  overflow-hidden" />
                                <div className="w-6 h-6 relative  overflow-hidden" />
                            </div>
                            <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="images/icon/x.png" onClick={handleCloseDialog} />
                        </div>
                        <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                            <iframe
                                src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                className="w-full h-full border-0"
                                style={{ border: 'none' }}
                                title="PDF Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}
        </Template>
    );
};

export default CadastrarContaReceber;
